import { createRoute, useNavigate } from '@tanstack/react-router'
import { useContext } from 'react'
import {
  Calendar,
  Check,
  Clock,
  ExternalLink,
  FileText,
  MapPin,
  Pencil,
  Tag,
  Video,
  X,
} from 'lucide-react'
import rootRoute from './rootRoute'
import type { Event } from '@/lib/types'
import {
  BackLink,
  Badge,
  Button,
  Card,
  Row,
  Stack,
  spacing,
} from '@/components/ui'
import { MetaRow } from '@/components/MetaRow'
import EventBanner from '@/components/EventBanner'
import MiniMap from '@/components/MiniMap'
import DateWarning from '@/components/DateWarning'
import { LoginContext } from '@/lib/auth'
import NotFound from '@/components/NotFound'
import Loader from '@/components/Loader'
import MarkdownContent from '@/components/MarkdownContent'
import EntityChip from '@/components/EntityChip'
import { $api } from '@/lib/fetch'
import { catOf } from '@/lib/types'
import {
  isGeocoded,
  isLikelyInvalidDate,
  prettyDate,
  safeHref,
  shortTime,
  splitAddress,
} from '@/lib/helpers'

export function EventDetail({
  event,
  isAdmin,
  onApprove,
  onReject,
  actionBusy,
}: {
  event: Event
  isAdmin: boolean
  onApprove?: () => void
  onReject?: () => void
  actionBusy?: boolean
}) {
  const showValidationActions = !!onApprove && !!onReject
  const navigate = useNavigate()
  const cat = catOf(event.type.type)
  const { venue, city } = splitAddress(event.location.address)
  const geocoded = isGeocoded(event.location)

  return (
    <div
      style={{
        maxWidth: 'var(--container-max)',
        margin: '0 auto',
        padding: 'var(--space-4) var(--gutter) var(--space-20)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
        }}
      >
        <BackLink onClick={() => navigate({ to: '/events', search: {} })}>
          Retour aux événements
        </BackLink>
        {isAdmin && !showValidationActions && (
          <Button
            variant="secondary"
            size="sm"
            iconLeft={<Pencil size={15} />}
            onClick={() =>
              navigate({
                to: '/events/$id/edit',
                params: { id: String(event.id) },
              })
            }
          >
            Modifier
          </Button>
        )}
      </div>

      <div
        className="oumma-event-banner"
        style={{
          position: 'relative',
          background: 'var(--emerald-700)',
          borderRadius: 'var(--radius-xl)',
          overflow: 'hidden',
          padding: 'var(--space-8) var(--space-6) var(--space-6)',
          marginTop: 'var(--space-4)',
        }}
      >
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            backgroundImage: "url('/assets/zellij-tile.svg')",
            backgroundSize: '116px 116px',
            opacity: 0.16,
            pointerEvents: 'none',
          }}
        />
        <div
          style={{
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 'var(--space-3)',
            textAlign: 'center',
          }}
        >
          <Badge
            tone={cat.tone}
            solid
            iconLeft={<span>{event.type.emoji}</span>}
          >
            {event.type.type}
          </Badge>
          <h1
            style={{
              margin: 0,
              color: 'var(--paper-0)',
              fontFamily: 'var(--font-display)',
              fontSize: 'var(--text-h1)',
              lineHeight: 1.1,
            }}
          >
            {event.title}
          </h1>
        </div>
      </div>

      <div className="oumma-event-layout">
        <div className="oumma-event-right">
          <div className="oumma-area-image">
            <div
              style={{
                borderRadius: 'var(--radius-lg)',
                border: '1px solid var(--gold-400)',
                outline: '1px solid var(--gold-200)',
                outlineOffset: 3,
                boxShadow: 'var(--shadow-gold)',
                overflow: 'hidden',
              }}
            >
              <EventBanner event={event} zoomable hideBadge />
            </div>
          </div>

          {isAdmin && event.image_ocr && (
            <Card
              variant="framed"
              padding={20}
              style={{ marginTop: spacing.lg }}
            >
              <Stack gap="sm">
                <Row gap="sm" align="center">
                  <FileText size={16} color="var(--ink-2)" />
                  <div
                    className="oumma-overline"
                    style={{ color: 'var(--ink-2)' }}
                  >
                    Texte OCR
                  </div>
                </Row>
                <pre
                  style={{
                    margin: 0,
                    fontFamily: 'var(--font-mono)',
                    fontSize: 'var(--text-sm)',
                    color: 'var(--ink-1)',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    maxHeight: 320,
                    overflowY: 'auto',
                  }}
                >
                  {event.image_ocr}
                </pre>
              </Stack>
            </Card>
          )}

          <div className="oumma-area-meta">
            <Card variant="framed" padding={24}>
              <Stack gap="lg">
                <MetaRow icon={<Calendar size={20} />} label="Date">
                  {isLikelyInvalidDate(event.start_date) ? (
                    <DateWarning />
                  ) : (
                    prettyDate(event.start_date)
                  )}
                </MetaRow>
                {!isLikelyInvalidDate(event.start_date) && (
                  <MetaRow icon={<Clock size={20} />} label="Heure">
                    {event.end_date
                      ? `${shortTime(event.start_date)} – ${shortTime(event.end_date)}`
                      : shortTime(event.start_date)}
                  </MetaRow>
                )}
                <MetaRow icon={<Tag size={20} />} label="Type">
                  {event.type.type}
                </MetaRow>
                {safeHref(event.video_conferencing_link) && (
                  <MetaRow icon={<Video size={20} />} label="En ligne">
                    <a
                      href={safeHref(event.video_conferencing_link)}
                      target="_blank"
                      rel="noreferrer noopener"
                      style={{ color: 'var(--emerald-700)', fontWeight: 600 }}
                    >
                      Lien visio
                    </a>
                  </MetaRow>
                )}
              </Stack>
            </Card>
          </div>
        </div>

        <div className="oumma-event-left">
          {event.entity && (
            <div
              className="oumma-area-organizer"
              style={{
                paddingBottom: 22,
                borderBottom: '1px solid var(--rule)',
              }}
            >
              <EntityChip
                entity={event.entity}
                size={46}
                tone={cat.tone}
                ring
                label="Organisé par"
              />
            </div>
          )}

          <div className="oumma-area-about">
            <h2
              style={{
                fontSize: 'var(--text-h3)',
                marginTop: 0,
                marginBottom: 12,
              }}
            >
              À propos de ce rassemblement
            </h2>
            <div style={{ padding: 'var(--space-3) var(--space-5)' }}>
              <MarkdownContent>{event.description}</MarkdownContent>
            </div>

            {safeHref(event.external_link) && (
              <a
                href={safeHref(event.external_link)}
                target="_blank"
                rel="noreferrer noopener"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  marginTop: 18,
                  color: 'var(--emerald-700)',
                  fontWeight: 600,
                  fontSize: 15.5,
                }}
              >
                <ExternalLink size={17} /> Page de l'organisateur
              </a>
            )}

            {showValidationActions && (
              <Row gap="md" wrap style={{ marginTop: 'var(--space-6)' }}>
                <Button
                  variant="primary"
                  iconLeft={<Check size={18} />}
                  onClick={onApprove}
                  loading={actionBusy}
                  style={{ flex: 1, minWidth: 150 }}
                >
                  Approuver
                </Button>
                <Button
                  variant="secondary"
                  iconLeft={<X size={18} />}
                  onClick={onReject}
                  loading={actionBusy}
                  style={{
                    flex: 1,
                    minWidth: 150,
                    color: 'var(--danger)',
                    borderColor: 'var(--danger-tint)',
                  }}
                >
                  Refuser
                </Button>
                {isAdmin && (
                  <Button
                    variant="secondary"
                    iconLeft={<Pencil size={18} />}
                    onClick={() =>
                      navigate({
                        to: '/events/$id/edit',
                        params: { id: String(event.id) },
                      })
                    }
                    style={{ flex: 1, minWidth: 150 }}
                  >
                    Modifier
                  </Button>
                )}
              </Row>
            )}

            <h2
              style={{
                fontSize: 'var(--text-h3)',
                marginTop: 34,
                marginBottom: 14,
              }}
            >
              Où
            </h2>
            <Card padding={0} variant="tint">
              {geocoded && (
                <div
                  style={{
                    position: 'relative',
                    height: 220,
                    overflow: 'hidden',
                    borderRadius: 'var(--radius-lg) var(--radius-lg) 0 0',
                  }}
                >
                  <MiniMap
                    longitude={event.location.longitude}
                    latitude={event.location.latitude}
                    color={cat.color}
                  />
                </div>
              )}
              <Row
                justify="space-between"
                gap="md"
                wrap
                style={{ padding: 18 }}
              >
                <div>
                  <div
                    style={{
                      fontWeight: 600,
                      color: 'var(--ink-0)',
                      fontSize: 16,
                    }}
                  >
                    {venue}
                  </div>
                  {city && (
                    <div style={{ color: 'var(--ink-2)', fontSize: 14.5 }}>
                      {city}
                    </div>
                  )}
                </div>
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                    event.location.address,
                  )}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <Button
                    variant="secondary"
                    size="sm"
                    iconLeft={<MapPin size={16} />}
                  >
                    Itinéraire
                  </Button>
                </a>
              </Row>
            </Card>
          </div>
        </div>
      </div>
    </div>
  )
}

const EventDetailPage = () => {
  const ctx = useContext(LoginContext)
  const isAdmin = ctx?.user?.admin || false
  const { id } = eventsDetailRoute.useParams()
  const numId = parseInt(id, 10)

  const { isPending, error, data } = $api.useQuery(
    'get',
    '/api/events/id/{id}',
    {
      params: { path: { id: numId } },
    },
    { enabled: !Number.isNaN(numId) },
  )

  if (Number.isNaN(numId)) return <NotFound />

  if (isPending) return <Loader />

  if (error) {
    if ((error as any)?.status === 404) return <NotFound />
    return (
      <div style={{ padding: 'var(--space-20)', textAlign: 'center' }}>
        Erreur inattendue. Veuillez réessayer.
      </div>
    )
  }

  return <EventDetail event={data.event} isAdmin={isAdmin} />
}

export const eventsDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/events/$id',
}).lazy(() => import('./EventDetailPage.lazy').then((m) => m.Route))

export default EventDetailPage
